
import React, { Component } from 'react';
import type { Dispatch } from 'redux';

import logger from '../../../app/logger';
import { getActiveDOMDocument } from '../../../base/config/functions.any';
import { isMobileBrowser } from '../../../base/environment/utils';
import { isLocalParticipantModerator } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { getVerticalFilmstripVisibleAreaWidth } from '../../../filmstrip/functions.web';
import { getToolboxHeight } from '../../../toolbox/functions.web';
import { LAYOUTS, getCurrentLayout } from '../../../video-layout';
import { getSharedDocumentUrl } from '../../functions';

/**
 * The type of the React {@code Component} props of {@link Etherpad}.
 */
type Props = {

    /**
     * The Redux dispatch function.
     */
    dispatch: Dispatch<any>,

    /**
     * URL for the etherpad document.
     */
    _documentUrl: string,

    /**
     * Whether the etherpad is visible.
     */
    _isVisible: boolean,

    /**
     * Whether the local participant is a moderator.
     */
    _isModerator: boolean,

    /**
     * The current layout mode.
     */
    _currentLayout: string,

    /**
     * The filmstrip width (vertical mode).
     */
    _verticalFilmstripWidth: number,

    /**
     * The filmstrip height (horizontal mode).
     */
    _filmstripHeight: number,
};

/**
 * Implements a React component for the etherpad iframe container.
 *
 * @augments Component
 */
class Etherpad extends Component<Props> {
    _iframeRef: Object;
    _containerRef: Object;
    _bodyBackground: string;

    /**
     * Initializes a new {@code Etherpad} instance.
     *
     * @param {Props} props - The React {@code Component} props.
     */
    constructor(props: Props) {
        super(props);

        this._iframeRef = React.createRef();
        this._containerRef = React.createRef();
        this._bodyBackground = '';

        this._onIframeLoad = this._onIframeLoad.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}.
     *
     * @inheritdoc
     */
    componentDidMount() {
        window.addEventListener('resize', this._handleResize);
        this._handleResize();
    }

    /**
     * Implements React's {@link Component#componentDidUpdate()}.
     *
     * @inheritdoc
     */
    componentDidUpdate(prevProps: Props) {
        if (this.props._isVisible !== prevProps._isVisible) {
            if (this.props._isVisible) {
                this._show();
            } else {
                this._hide();
            }
        }

        this._handleResize();
    }

    /**
     * Implements React's {@link Component#componentWillUnmount()}.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        window.removeEventListener('resize', this._handleResize);
        this._restoreBodyBackground();
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const { _documentUrl, _isVisible, _isModerator } = this.props;

        const containerStyle = {
            zIndex: _isVisible ? '10' : '0',
            position: _isVisible ? 'relative' : 'absolute',
            pointerEvents: _isVisible && !_isModerator ? 'none' : 'auto'
        };

        const iframeStyle = {
            width: '100%',
            height: '100%',
            visibility: _isVisible ? 'visible' : 'hidden',
            display: _isVisible ? 'block' : 'none',
            opacity: _isVisible ? '1' : '0',
            transition: 'opacity 300ms',
            border: '0',
            position: 'absolute',
            top: 0,
            left: 0
        };

        return (
            <div
                id = 'etherpad'
                ref = { this._containerRef }
                style = { containerStyle }>
                <iframe
                    frameBorder = '0'
                    id = 'etherpadIFrame'
                    onLoad = { this._onIframeLoad }
                    ref = { this._iframeRef }
                    scrolling = 'no'
                    src = { _documentUrl }
                    style = { iframeStyle }
                    title = 'Etherpad' />
            </div>
        );
    }

    _onIframeLoad: () => void;

    /**
     * Handles iframe load event to enable mouse event bubbling.
     *
     * @private
     * @returns {void}
     */
    _onIframeLoad() {
        const iframe = this._iframeRef.current;

        if (!iframe) {
            return;
        }

        try {


            this._bubbleIframeMouseMove(iframe);

            setTimeout(() => {
                const doc = iframe.contentDocument;

                if (!doc) {
                    return;
                }

                // The iframes inside of the etherpad are not yet loaded
                // when the etherpad iframe is loaded
                const outer = doc.getElementsByName('ace_outer')[0];

                if (outer) {
                    this._bubbleIframeMouseMove(outer);
                }

                const inner = doc.getElementsByName('ace_inner')[0];

                if (inner) {
                    this._bubbleIframeMouseMove(inner);
                }
            }, 2000);
        } catch (error) {
            // Cross-origin restrictions may prevent access

            logger.warn('Unable to access iframe content:', error);
        }
    }

    _bubbleIframeMouseMove: (iframe: HTMLIFrameElement) => void;

    /**
     * Bubbles mouse move events from iframe to parent document.
     *
     * @param {HTMLIFrameElement} iframe - The iframe element.
     * @private
     * @returns {void}
     */
    _bubbleIframeMouseMove(iframe) {
        if (!iframe.contentWindow) {
            return;
        }

        const existingOnMouseMove = iframe.contentWindow.onmousemove;

        iframe.contentWindow.onmousemove = function(e) {
            if (existingOnMouseMove) {
                existingOnMouseMove(e);
            }
            const evt = document.createEvent('MouseEvents');
            const boundingClientRect = iframe.getBoundingClientRect();

            evt.initMouseEvent(
                'mousemove',
                true, // bubbles
                false, // not cancelable
                window,
                e.detail,
                e.screenX,
                e.screenY,
                e.clientX + boundingClientRect.left,
                e.clientY + boundingClientRect.top,
                e.ctrlKey,
                e.altKey,
                e.shiftKey,
                e.metaKey,
                e.button,
                null // no related element
            );
            iframe.dispatchEvent(evt);
        };
    }

    _handleResize: () => void;

    /**
     * Handles window resize events.
     *
     * @private
     * @returns {void}
     */
    _handleResize = () => {
        if (!this._iframeRef.current || !this.props._isVisible) {
            return;
        }

        const state = APP.store.getState();
        const activeWindow = getActiveDOMDocument(state);
        const container = activeWindow.getElementById('largeVideoContainer');

        if (!container) {
            return;
        }

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        const { _currentLayout, _verticalFilmstripWidth, _filmstripHeight } = this.props;

        let height, width;

        if (_currentLayout === LAYOUTS.VERTICAL_FILMSTRIP_VIEW) {
            height = containerHeight - getToolboxHeight();
            width = containerWidth - _verticalFilmstripWidth - (isMobileBrowser() ? 0 : 60);
        } else {
            height = containerHeight - _filmstripHeight;
            width = containerWidth - (isMobileBrowser() ? 0 : 60);
        }

        this._iframeRef.current.style.width = `${width}px`;
        this._iframeRef.current.style.height = `${height}px`;
    };

    _show: () => void;

    /**
     * Shows the iframe with fade-in effect.
     *
     * @private
     * @returns {void}
     */
    _show = () => {
        if (!this._iframeRef.current) {
            return;
        }

        // Save current body background
        this._bodyBackground = document.body.style.background;

        // Apply fade-in effect
        setTimeout(() => {
            document.body.style.background = '#eeeeee';
        }, 300);

        this._handleResize();
    };

    _hide: () => void;

    /**
     * Hides the iframe with fade-out effect.
     *
     * @private
     * @returns {void}
     */
    _hide = () => {
        this._restoreBodyBackground();

        const dominantSpeaker = document.getElementById('dominantSpeaker');

        if (dominantSpeaker) {
            dominantSpeaker.style.visibility = 'visible';
        }
    };

    _restoreBodyBackground: () => void;

    /**
     * Restores the original body background.
     *
     * @private
     * @returns {void}
     */
    _restoreBodyBackground = () => {
        if (this._bodyBackground) {
            document.body.style.background = this._bodyBackground;
        }
    };
}

/**
 * Maps (parts of) the redux state to {@link Etherpad} React {@code Component} props.
 *
 * @param {Object} state - The redux store/state.
 * @private
 * @returns {Object}
 */
function _mapStateToProps(state: Object) {
    const { editing } = state['features/etherpad'];
    const documentUrl = getSharedDocumentUrl(state);
    const currentLayout = getCurrentLayout(state);

    return {
        _documentUrl: documentUrl,
        _isVisible: editing,
        _isModerator: isLocalParticipantModerator(state),
        _currentLayout: currentLayout,
        _verticalFilmstripWidth: getVerticalFilmstripVisibleAreaWidth(),
        _filmstripHeight: state['features/filmstrip'].horizontalViewDimensions?.filmstripHeight || 0
    };
}

export default connect(_mapStateToProps)(Etherpad);
